import { APP_VERSION } from "@shared/index";
import type { UpdateStatus } from "@shared/index";
import { useEffect, useState } from "react";

function isDesktop(): boolean {
  return Boolean(window.steeldartDesktop?.checkUpdates);
}

function rememberPicked(next: UpdateStatus, setPicked: (value: string | ((cur: string) => string)) => void): void {
  setPicked((current) => {
    if (!current && next.latest?.version) return next.latest.version;
    if (current && next.releases.length && !next.releases.some((r) => r.version === current) && next.latest?.version) {
      return next.latest.version;
    }
    return current;
  });
}

function useDesktopUpdate() {
  const desktop = window.steeldartDesktop;
  const [status, setStatus] = useState<UpdateStatus | null>(null);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!desktop?.getUpdateStatus) return;
    let cancelled = false;
    void desktop.getUpdateStatus().then((next) => {
      if (cancelled) return;
      rememberPicked(next, setPicked);
      setStatus(next);
    });
    const stop = desktop.onUpdateStatus?.((next) => {
      rememberPicked(next, setPicked);
      setStatus(next);
      if (next.phase === "ready") setConfirmRestart(true);
      if (next.phase === "downloading") setDismissed(false);
    });
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [desktop]);

  const run = async (action: () => Promise<UpdateStatus | undefined> | undefined) => {
    setBusy(true);
    try {
      const next = await action();
      if (next) {
        rememberPicked(next, setPicked);
        setStatus(next);
        if (next.phase === "ready") setConfirmRestart(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return {
    desktop,
    status,
    picked,
    setPicked,
    busy,
    confirmRestart,
    setConfirmRestart,
    dismissed,
    setDismissed,
    check: () => run(() => desktop?.checkUpdates?.()),
    download: (version?: string) => run(() => desktop?.downloadUpdate?.(version || picked || undefined)),
    apply: () => run(() => desktop?.applyUpdate?.()),
  };
}

function percent(status: UpdateStatus | null): string | null {
  if (!status || status.phase !== "downloading") return null;
  if (!status.total) return "Lade Update …";
  return `Lade Update … ${Math.min(100, Math.round((100 * (status.received ?? 0)) / status.total))} %`;
}

function UpdateRestartDialog({
  version,
  busy,
  error,
  onConfirm,
  onLater,
}: {
  version: string;
  busy: boolean;
  error?: string | null;
  onConfirm: () => void;
  onLater: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 p-6">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-ink-800 p-6 text-left">
        <h2 className="font-display text-3xl text-white">Update geladen</h2>
        <p className="mt-3 text-sm text-slate-400">
          Version {version} ist bereit. Die App startet neu. Einstellungen, Spielstand und Statistik bleiben erhalten.
        </p>
        {error ? <p className="mt-3 text-sm text-crimson">{error}</p> : null}
        <button
          type="button"
          className="mt-6 min-h-touch w-full rounded-2xl bg-amber-glow font-bold text-ink-950 disabled:opacity-60"
          disabled={busy}
          onClick={onConfirm}
        >
          Jetzt neu starten
        </button>
        <button type="button" className="mt-3 min-h-touch w-full rounded-2xl bg-ink-700 font-bold text-white" onClick={onLater}>
          Später
        </button>
      </div>
    </div>
  );
}

export function DesktopUpdateBanner() {
  const { status, busy, confirmRestart, setConfirmRestart, dismissed, setDismissed, download, apply } = useDesktopUpdate();
  if (!isDesktop() || dismissed) return null;
  const newer = status?.phase === "available" && status.latest?.newer ? status.latest.version : null;
  const downloading = percent(status);
  if (!newer && !downloading && status?.phase !== "ready") return null;
  const version = status?.downloadedVersion || newer || "";
  return (
    <>
      <div className="sticky top-0 z-40 flex flex-wrap items-center justify-center gap-3 bg-amber-glow px-4 py-2 text-sm font-bold text-ink-950">
        <span>{downloading || `Neue Version ${version} verfügbar`}</span>
        {status?.phase !== "downloading" && status?.phase !== "applying" && (
          <button
            type="button"
            className="rounded-xl bg-ink-950 px-3 py-1 text-xs font-black uppercase tracking-wide text-amber-glow disabled:opacity-50"
            disabled={busy}
            onClick={() => void (status?.phase === "ready" ? apply() : download(version))}
          >
            Aktualisieren
          </button>
        )}
        <button type="button" className="text-xs font-bold uppercase tracking-wide text-ink-950/70" onClick={() => setDismissed(true)}>
          Später
        </button>
      </div>
      {confirmRestart && status?.downloadedVersion && (
        <UpdateRestartDialog
          version={status.downloadedVersion}
          busy={busy}
          error={status.error}
          onConfirm={() => void apply()}
          onLater={() => setConfirmRestart(false)}
        />
      )}
    </>
  );
}

export function DesktopUpdateCompact() {
  const { status, busy, check, download, apply } = useDesktopUpdate();
  if (!isDesktop()) return null;
  const newer = status?.latest?.newer ? status.latest.version : null;
  return (
    <div className="mt-4 rounded-2xl bg-ink-700 px-4 py-3 text-sm">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Aktualisierung</div>
      <div className="mt-1 text-slate-200">Version {APP_VERSION}</div>
      {newer ? (
        <p className="mt-1 font-semibold text-amber-glow">Neue Version {newer} verfügbar</p>
      ) : status?.phase === "none" ? (
        <p className="mt-1 text-slate-400">Aktuell</p>
      ) : null}
      {status?.needToken && <p className="mt-1 text-crimson">{status.error}</p>}
      {status?.error && !status.needToken && <p className="mt-1 text-crimson">{status.error}</p>}
      {percent(status) && <p className="mt-1 text-amber-glow">{percent(status)}</p>}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          className="min-h-touch flex-1 rounded-xl bg-amber-glow text-xs font-bold text-ink-950 disabled:opacity-50"
          disabled={busy}
          onClick={() => void (status?.phase === "ready" ? apply() : newer ? download(newer) : check())}
        >
          {status?.phase === "ready" ? "Neu starten" : newer ? "Aktualisieren" : "Prüfen"}
        </button>
      </div>
    </div>
  );
}

export function DesktopUpdatePanel({ githubTokenSet = false }: { githubTokenSet?: boolean }) {
  const {
    desktop,
    status,
    picked,
    setPicked,
    busy,
    check,
    download,
    apply,
  } = useDesktopUpdate();
  const [token, setToken] = useState("");
  const [tokenSaved, setTokenSaved] = useState(githubTokenSet);
  const [tokenBusy, setTokenBusy] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  useEffect(() => {
    setTokenSaved(githubTokenSet);
  }, [githubTokenSet]);

  useEffect(() => {
    void desktop?.getState?.().then((state) => {
      if (state.githubTokenSet) setTokenSaved(true);
    });
  }, [desktop]);

  if (!isDesktop()) return null;

  const saveToken = async () => {
    if (!desktop?.saveSettings || !token.trim()) return;
    setTokenBusy(true);
    setTokenError(null);
    const result = await desktop.saveSettings({ githubToken: token.trim() });
    setTokenBusy(false);
    if (!result.ok) {
      setTokenError(result.error);
      return;
    }
    setToken("");
    setTokenSaved(true);
    void check();
  };

  const newer = status?.latest?.newer ? status.latest.version : null;
  const options = status?.releases ?? [];

  return (
    <div className="mt-6 rounded-2xl bg-ink-950 p-4">
      <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">Aktualisierung</div>
      <p className="mt-2 text-sm text-slate-300">Aktuelle Version {APP_VERSION}</p>
      {newer && <p className="mt-1 font-semibold text-amber-glow">Neue Version {newer} verfügbar</p>}
      {status?.phase === "none" && <p className="mt-1 text-sm text-slate-400">Keine neuere Version gefunden.</p>}
      {(status?.error || tokenError) && <p className="mt-2 text-sm text-crimson">{tokenError || status?.error}</p>}
      {percent(status) && <p className="mt-2 text-sm text-amber-glow">{percent(status)}</p>}

      <label className="mt-4 block text-sm text-slate-400">GitHub-Token (privates Repo)</label>
      <input
        type="password"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder={tokenSaved || status?.tokenSet ? "gespeichert — neu eingeben zum Ändern" : "classic PAT mit Recht repo"}
        autoComplete="off"
        className="mt-1 min-h-touch w-full rounded-2xl bg-ink-800 px-4 outline-none ring-amber-glow/40 focus:ring"
      />
      <p className="mt-2 text-xs text-slate-500">
        Ohne Token versucht die App einen öffentlichen Abruf. Bei 404 das Token speichern — classic PAT, Leserecht
        „repo“, reicht für private Release-Dateien.
      </p>
      <button
        type="button"
        className="mt-2 min-h-touch rounded-2xl bg-ink-700 px-4 text-sm font-bold disabled:opacity-50"
        disabled={tokenBusy || !token.trim()}
        onClick={() => void saveToken()}
      >
        Token speichern
      </button>

      {options.length > 0 && (
        <>
          <label className="mt-4 block text-sm text-slate-400">Version wählen</label>
          <select
            value={picked || newer || options[0]?.version || ""}
            onChange={(e) => setPicked(e.target.value)}
            className="mt-1 min-h-touch w-full rounded-2xl bg-ink-800 px-4 outline-none ring-amber-glow/40 focus:ring"
          >
            {options.map((release) => (
              <option key={release.version} value={release.version}>
                {release.version}
                {release.newer ? " — neuer" : release.version === APP_VERSION ? " — aktuell" : ""}
              </option>
            ))}
          </select>
        </>
      )}

      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <button
          type="button"
          className="min-h-touch rounded-2xl bg-ink-700 font-bold text-white disabled:opacity-50"
          disabled={busy}
          onClick={() => void check()}
        >
          Auf Updates prüfen
        </button>
        <button
          type="button"
          className="min-h-touch rounded-2xl bg-amber-glow font-bold text-ink-950 disabled:opacity-50"
          disabled={busy || (!picked && !newer && status?.phase !== "ready")}
          onClick={() => void (status?.phase === "ready" ? apply() : download(picked || newer || undefined))}
        >
          {status?.phase === "ready" ? "Neu starten" : "Aktualisieren"}
        </button>
      </div>
      <p className="mt-3 text-xs text-slate-500">
        Windows ersetzt die Datei, mit der du die App gestartet hast, und startet sie neu. Am Pi wird der
        Programmordner getauscht. userData (~/.config bzw. AppData) bleibt unberührt.
      </p>
    </div>
  );
}
